import React, { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Alert, Button, Col, Form, Input, message, Row, Space, Table, Upload } from 'antd';
import { SendOutlined, UploadOutlined } from '@ant-design/icons';

import ClientVersionSelect from '@lib/components/client-version-select';
import EnvironmentSelect from '@lib/components/env-select';
import { TimestampDatePicker } from '@lib/components/ui/date-picker';
import { CsInboxHistoryLocation } from '@lib/routes/routes';

import { TaskStatus } from '@services/_api';
import { registerBatchRewardsTask } from '@services/cstool/inbox-batch-players';

type PlayerRewardItem = {
  itemId: string;
  quantity: number;
  rewardType: number;
};

type PlayerMailRewards = {
  playerId: string;
  rewards: PlayerRewardItem[];
};

// com.alleylabs.game.type.RewardType, mirrored from base's cs-inbox-v3.handler.ts
// (CS_INBOX_MESSAGE_EXTRA_PROTOS) - shown here purely as a hint since this tab's bulk input
// takes the raw numeric value directly, with no dropdown to pick it from.
const REWARD_TYPE_HELP =
  'rewardType: 0 LOOTBOX, 1 CURRENCY, 2 COMMANDER, 3 CARD_UPGRADE, 4 UNLOCK_CARD, ' +
  '5 CARD_UPGRADE_RANDOM, 6 UNLOCK_CARD_RANDOM, 7 CARD_PACK, 8 SHOPKEEPER, ' +
  '9 CARD_STAT_UPGRADE, 10 RACE_ABILITY_UPGRADE, 11 STATIC_CARD_STAT_UPGRADE';

const PLAYERS_PLACEHOLDER = JSON.stringify(
  [{ playerId: '123456', rewards: [{ itemId: 'gold', quantity: 100, rewardType: 1 }] }],
  null,
  2,
);

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsText(file);
  });

// Parses the pasted/uploaded JSON array into a strict { playerId, rewards[] }[] shape,
// accepting a couple of common casing variants per field so a file exported from elsewhere
// (snake_case, PascalCase) doesn't need hand-editing first.
const parsePlayerMails = (text: string): PlayerMailRewards[] => {
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Not valid JSON.');
  }

  if (!Array.isArray(parsed)) {
    throw new Error('Expected a JSON array of { playerId, rewards } items.');
  }

  return parsed.map((item, index) => {
    const playerId = item?.playerId ?? item?.PlayerId ?? item?.player_id;
    if (!playerId || typeof playerId !== 'string') {
      throw new Error(`Item ${index + 1}: missing "playerId"`);
    }

    const rewardsRaw = item?.rewards;
    if (!Array.isArray(rewardsRaw) || !rewardsRaw.length) {
      throw new Error(`Item ${index + 1} (${playerId}): "rewards" must be a non-empty array`);
    }

    const rewards = rewardsRaw.map((reward: any, rewardIndex: number) => {
      const itemId = reward?.itemId ?? reward?.item_id;
      const quantity = Number(reward?.quantity);
      const rewardType = Number(reward?.rewardType ?? reward?.reward_type);

      if (!itemId || typeof itemId !== 'string') {
        throw new Error(
          `Item ${index + 1} (${playerId}), reward ${rewardIndex + 1}: missing "itemId"`,
        );
      }
      if (!Number.isFinite(quantity) || quantity <= 0) {
        throw new Error(
          `Item ${index + 1} (${playerId}), reward ${
            rewardIndex + 1
          }: "quantity" must be a positive number`,
        );
      }
      if (!Number.isInteger(rewardType) || rewardType < 0) {
        throw new Error(
          `Item ${index + 1} (${playerId}), reward ${
            rewardIndex + 1
          }: "rewardType" must be a non-negative integer`,
        );
      }

      return { itemId, quantity, rewardType };
    });

    return { playerId, rewards };
  });
};

const InboxBatchRewardsBody: FC = () => {
  const history = useHistory();

  const [environment, setEnvironment] = useState<string>();
  const [blueprintVersion, setBlueprintVersion] = useState<string>();
  const [mailTemplate, setMailTemplate] = useState<string>();
  const [createdTime, setCreatedTime] = useState<number>();
  const [players, setPlayers] = useState<PlayerMailRewards[]>([]);
  const [parseError, setParseError] = useState<string>();
  const [loading, setLoading] = useState(false);

  const applyPlayersText = (text: string) => {
    if (!text.trim()) {
      setPlayers([]);
      setParseError(undefined);
      return;
    }
    try {
      setPlayers(parsePlayerMails(text));
      setParseError(undefined);
    } catch (err) {
      setPlayers([]);
      setParseError((err as Error).message);
    }
  };

  const canSubmit = !!environment && !!mailTemplate && !!createdTime && players.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      message.error('Environment, Mail Template Id, Created Time and Players are all required!');
      return;
    }

    setLoading(true);
    const res = await registerBatchRewardsTask({
      environment: environment as string,
      blueprintVersion,
      mailTemplate: mailTemplate as string,
      createdTime: createdTime as number,
      players,
    });
    if (res.ok) {
      const rows = res.data ?? [];
      const failedCount = rows.filter((row) => row.status === TaskStatus.FAIL).length;
      if (failedCount) {
        message.warning(
          `Sent with ${failedCount}/${rows.length} batch(es) failed - check Inbox History for details.`,
        );
      } else {
        message.success(`Rewards sent to ${players.length} player(s) successfully!`);
      }

      history.push({
        pathname: CsInboxHistoryLocation.path,
        search: `?environment=${environment}`,
      });
    }
    setLoading(false);
  };

  return (
    <div className="ant-form ant-form-vertical" style={{ maxWidth: '100%' }}>
      <Row>
        <Col flex="0 0 220px">
          <Form.Item label="Environment" rules={[{ required: true }]}>
            <EnvironmentSelect onChange={setEnvironment} />
          </Form.Item>
        </Col>
        <Col flex="0 0 220px">
          <Form.Item label="Client Version" style={{ marginLeft: 10 }}>
            <ClientVersionSelect environment={environment} onChange={setBlueprintVersion} />
          </Form.Item>
        </Col>
        <Col flex="0 0 260px">
          <Form.Item label="Mail Template Id" required style={{ marginLeft: 10 }}>
            <Input value={mailTemplate} onChange={(e) => setMailTemplate(e.target.value)} />
          </Form.Item>
        </Col>
        <Col flex="0 0 240px">
          <Form.Item label="Created Time (UTC+0)" required style={{ marginLeft: 10 }}>
            <TimestampDatePicker
              showTime
              useSeconds
              utcTime
              style={{ width: '100%' }}
              value={createdTime}
              onChange={(value) => setCreatedTime(value ?? undefined)}
            />
          </Form.Item>
        </Col>
        <Col flex="0 0 auto" style={{ marginLeft: 15 }}>
          <div style={{ marginTop: 30 }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={loading}
            >
              Submit
            </Button>
          </div>
        </Col>
      </Row>

      <Form.Item
        label={`Players & Rewards - JSON array, one entry per player. ${REWARD_TYPE_HELP}`}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.TextArea
            rows={10}
            placeholder={PLAYERS_PLACEHOLDER}
            onChange={(e) => applyPlayersText(e.target.value)}
          />
          <Upload
            accept=".json"
            showUploadList={false}
            beforeUpload={async (file) => {
              try {
                applyPlayersText(await readFileAsText(file));
              } catch (err) {
                setPlayers([]);
                setParseError((err as Error).message);
              }
              // Always false - this only reads the file client-side to build the players
              // array, it should never let antd upload it anywhere.
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>Upload .json</Button>
          </Upload>
          {parseError && <Alert type="error" showIcon message={parseError} />}
          {!parseError && players.length > 0 && (
            <Alert
              type="success"
              showIcon
              message={`Parsed ${players.length} player(s), ${players.reduce(
                (sum, p) => sum + p.rewards.length,
                0,
              )} total reward row(s).`}
            />
          )}
        </Space>
      </Form.Item>

      {players.length > 0 && (
        <Table
          size="small"
          rowKey="playerId"
          dataSource={players}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          columns={[
            { title: 'Player Id', dataIndex: 'playerId', width: 220 },
            {
              title: 'Rewards',
              dataIndex: 'rewards',
              render: (rewards: PlayerRewardItem[]) =>
                rewards.map((r) => `${r.itemId} x${r.quantity} (type ${r.rewardType})`).join(', '),
            },
          ]}
        />
      )}
    </div>
  );
};

export default InboxBatchRewardsBody;
